import {useNavigate} from 'react-router-dom'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import type {ApproveItemRequest, RejectItemRequest} from '@/types/access-request'

export interface ApproveItemPayload {
    itemId: string
    body: ApproveItemRequest
}

export interface RejectItemPayload {
    itemId: string
    body: RejectItemRequest
}

export interface CancelItemPayload {
    itemId: string
}

export interface UseAccessRequestMutationsOptions {
    isMyRoute?: boolean
    onApproveSuccess?: () => void
    onRejectSuccess?: () => void
    onCancelItemSuccess?: () => void
    onCancelRequestSuccess?: () => void
}

/**
 * Composite mutation hook for the AccessRequestDetail page.
 *
 * Members:
 *  - approve         — approve a single PENDING_APPROVAL item (with optional comment)
 *  - reject          — reject a single PENDING_APPROVAL item (with required comment)
 *  - cancelItem      — cancel a single PENDING_APPROVAL item (no comment payload required)
 *  - cancelRequest   — cancel the entire request and navigate back to the list
 *
 * The 603.05 contract surface lists `submit` / `runTest` as members; access
 * requests have no equivalent backend endpoints (submit happens through the
 * wizard via {@link useAccessRequestCreate}; there is no test endpoint), so
 * those members are intentionally absent. 607.09's wizard imports
 * {@code useAccessRequestCreate} — separate from this hook because invalidation
 * + post-success navigation differ.
 */
export function useAccessRequestMutations(
    id: string | undefined,
    options: UseAccessRequestMutationsOptions = {},
) {
    const services = useServices()
    const navigate = useNavigate()
    const i18n = useI18n()
    const {
        isMyRoute = false,
        onApproveSuccess,
        onRejectSuccess,
        onCancelItemSuccess,
        onCancelRequestSuccess,
    } = options

    /* eslint-disable i18next/no-literal-string -- TanStack Query keys, not user-visible strings */
    const invalidateBoth = [['access-request', id], ['my-requests']] as const
    /* eslint-enable i18next/no-literal-string */

    const approve = useEntityMutation({
        mutationFn: (payload: ApproveItemPayload) =>
            services.accessRequests.approveItem(payload.itemId, payload.body),
        invalidate: [...invalidateBoth],
        successMessage: i18n.accessRequests.itemApproved,
        onSuccess: () => onApproveSuccess?.(),
    })

    const reject = useEntityMutation({
        mutationFn: (payload: RejectItemPayload) =>
            services.accessRequests.rejectItem(payload.itemId, payload.body),
        invalidate: [...invalidateBoth],
        successMessage: i18n.accessRequests.itemRejected,
        onSuccess: () => onRejectSuccess?.(),
    })

    const cancelItem = useEntityMutation({
        mutationFn: (payload: CancelItemPayload) =>
            services.accessRequests.cancelItem(payload.itemId),
        invalidate: [...invalidateBoth],
        successMessage: i18n.accessRequests.itemCancelled,
        onSuccess: () => onCancelItemSuccess?.(),
    })

    const cancelRequest = useEntityMutation({
        mutationFn: () => services.accessRequests.cancel(id!),
        invalidate: [...invalidateBoth],
        successMessage: i18n.accessRequests.requestCancelled,
        onSuccess: () => {
            onCancelRequestSuccess?.()
            const target = isMyRoute
                ? routes.my.requests.list()
                : routes.accessGovernance.accessRequests.list()
            navigate(target)
        },
    })

    return {approve, reject, cancelItem, cancelRequest}
}
